import {
  Component,
  EventEmitter,
  Input,
  NO_ERRORS_SCHEMA,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import {ErrorMessageComponent} from '../../v1/error-component/error-message.component';
import {CommonModule} from '@angular/common';
import {NgxMaskDirective, provideNgxMask} from 'ngx-mask';
import {GateweeIconComponent} from '../gatewee-icon/gatewee-icon.component';
import {TranslatePipe} from '@ngx-translate/core';
import {GateweeSkeletonComponent} from '../../v1/gatewee-skeleton/gatewee-skeleton.component';
import {TuiHint, TuiIcon, TuiTextfield} from '@taiga-ui/core';
import {TuiPassword} from '@taiga-ui/kit';
import {WithdrawalAmountInputMode} from '@gatewee/utils';
import {GateweeCurrencyDigitsDirective} from '../gatewee-currency-digits/gatewee-currency-digits.directive';

export type GateweeInputSwitchVariant = 'currencyPercent' | 'withdrawalAmount';

@Component({
  selector: 'gatewee-input-v2',
  templateUrl: './gatewee-input-v2.component.html',
  styleUrls: ['./gatewee-input-v2.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ErrorMessageComponent,
    NgxMaskDirective,
    GateweeIconComponent,
    TranslatePipe,
    GateweeSkeletonComponent,
    TuiTextfield,
    GateweeCurrencyDigitsDirective,
    TuiIcon,
    TuiPassword,
    TuiHint,
  ],
  providers: [provideNgxMask()],
  schemas: [NO_ERRORS_SCHEMA],
})
export class GateweeInputV2Component implements OnInit, OnChanges {
  /** When provided, use reactive form control. When omitted, use standalone mode with value/valueChange (ngModel). */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @Input() form?: FormGroup | any;
  @Input() controlName?: string;
  /** Standalone mode: bound value (e.g. for filter inputs or ngModel-style binding). */
  @Input() value: string | number | object | null = '';
  @Input() label!: string;
  @Input() placeholder = '';
  @Input() autocomplete = false;
  @Input() id!: string;
  @Input() type:
    | 'text'
    | 'number'
    | 'decimal'
    | 'currency'
    /** Digit-cent currency entry (checkout-style); migrate from `currency` incrementally. */
    | 'currencyTaiga'
    | 'rate'
    | 'email'
    | 'password' = 'text';
  @Input() submitted = false;
  @Input() customMessages: Record<string, string> = {};
  @Input() mask = '';
  @Input() required = false;
  @Input() format: 'currency' | 'percent' | null = 'currency';
  @Input() max!: number | string;
  @Input() min!: number | string;
  @Input() hint!: {message: string; icon: string};
  @Input() info!: string;
  @Input() isLoading = false;
  @Input() switch = false;
  /** `currencyPercent`: BRL/% toggle (default). `withdrawalAmount`: add-fees vs deduct-fees. */
  @Input() switchVariant: GateweeInputSwitchVariant = 'currencyPercent';
  /** Bound mode when `switchVariant` is `withdrawalAmount`. */
  @Input() amountInputMode: WithdrawalAmountInputMode = 'addFees';
  /** Start icon for `tui-textfield` (Taiga resolver). Use `@tui.*` from app assets; `@phi.*` only works in `gatewee-icon`, not here. */
  @Input() iconName = '';
  @Input() readonly = false;
  /** ISO 4217 currency for Intl formatting (`currencyTaiga`, display helpers); default BRL. */
  @Input() currencyCode = 'BRL';
  @Output() blur = new EventEmitter<FocusEvent>();
  @Output() focus = new EventEmitter<FocusEvent>();
  @Output() currencyChange = new EventEmitter<boolean>();
  @Output() amountModeChange = new EventEmitter<WithdrawalAmountInputMode>();
  /** Standalone mode: emit when value changes (emits string for text type). */
  @Output() valueChange = new EventEmitter<string | number | object | null>();

  @Input() class = '';

  isCurrency = true;

  /** Internal form used when form/controlName are not provided (standalone mode). */
  private readonly standaloneControl = new FormControl<string | number | null>(
    '',
  );
  private readonly standaloneForm = new FormGroup({
    value: this.standaloneControl,
  });

  get useStandalone(): boolean {
    return !this.form || this.controlName === null || this.controlName === '';
  }

  get formGroup(): FormGroup {
    return this.useStandalone ? this.standaloneForm : this.form;
  }

  get control(): FormControl {
    if (this.useStandalone) {
      return this.standaloneControl;
    }
    const ctrl = this.form.get(this.controlName);
    if (!ctrl) {
      throw new Error(`Control "${this.controlName}" não existe`);
    }
    return ctrl as FormControl;
  }

  /** Leading currency glyph for `currencyTaiga` when `iconName` is not set (replaces icon slot). */
  get currencyTaigaLeadSymbol(): string {
    try {
      const parts = new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: this.currencyCode,
      }).formatToParts(0);
      return parts.find(p => p.type === 'currency')?.value ?? 'R$';
    } catch {
      return 'R$';
    }
  }

  ngOnInit(): void {
    if (this.useStandalone) {
      this.standaloneControl.setValue(
        this.standaloneValueFromInput(this.value),
      );
      this.standaloneControl.valueChanges.subscribe(v =>
        this.valueChange.emit(v as string | number | object | null),
      );
    }
    if (this.min && !this.useStandalone) {
      const ctrl = this.control;
      const validators = ctrl.validator ? [ctrl.validator] : [];
      ctrl.setValidators([
        ...validators,
        Validators.minLength(Number(this.min)),
      ]);
      ctrl.updateValueAndValidity();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.useStandalone && changes['value']) {
      const newVal = changes['value'].currentValue as
        | string
        | number
        | object
        | null
        | undefined;
      const toSet = this.standaloneValueFromInput(newVal);
      if (this.standaloneControl.value !== toSet) {
        this.standaloneControl.setValue(toSet, {emitEvent: false});
      }
    }
  }

  /** Coerce external value for the internal control. */
  private standaloneValueFromInput(
    v: string | number | object | null | undefined,
  ): string | number | null {
    if (this.type === 'currencyTaiga') {
      if (v === undefined || v === null || v === '') {
        return null;
      }
      if (typeof v === 'number') {
        return Number.isFinite(v) ? v : null;
      }
      if (typeof v === 'string') {
        return this.parseDecimal(v);
      }
      return null;
    }
    if (v === undefined || v === null) return '';
    if (typeof v === 'string' || typeof v === 'number') return v;
    return String(v);
  }

  get displayValue(): string {
    if (this.type === 'currency' || this.type === 'currencyTaiga') {
      const value = this.control.value;
      return this.formatCurrencyDisplay(value);
    }

    return this.control.value ?? '';
  }

  get hasValidation(): boolean {
    const ctrl = this.control;
    return !!ctrl.validator || !!ctrl.asyncValidator;
  }

  /** Whether validation feedback (stacked error) should reserve space below the field. */
  get showFeedbackError(): boolean {
    const ctrl = this.control;
    if (!ctrl?.invalid) {
      return false;
    }
    return this.submitted || ctrl.touched || ctrl.dirty;
  }

  get inputClasses(): Record<string, boolean> {
    return {
      normal: this.control.dirty || !!this.control.value,
      'is-invalid':
        this.hasValidation && this.control.touched && this.control.invalid,
      'is-disabled': this.control.disabled,
    };
  }

  get isWithdrawalAmountSwitch(): boolean {
    return this.switchVariant === 'withdrawalAmount';
  }

  get isAddFeesMode(): boolean {
    return this.amountInputMode !== 'deductFees';
  }

  get isSwitchKnobRight(): boolean {
    if (this.isWithdrawalAmountSwitch) {
      return this.isAddFeesMode;
    }
    return this.isCurrency;
  }

  get switchHintKey(): string {
    if (!this.isWithdrawalAmountSwitch) {
      return '';
    }
    return this.isAddFeesMode
      ? 'features.withdrawals.modalPix.amountInputMode.switchHintAddFees'
      : 'features.withdrawals.modalPix.amountInputMode.switchHintDeductFees';
  }

  get switchAriaLabelKey(): string {
    if (!this.isWithdrawalAmountSwitch) {
      return '';
    }
    return this.isAddFeesMode
      ? 'features.withdrawals.modalPix.amountInputMode.addFeesAria'
      : 'features.withdrawals.modalPix.amountInputMode.deductFeesAria';
  }

  onSwitchToggle(): void {
    if (this.isWithdrawalAmountSwitch) {
      const next: WithdrawalAmountInputMode = this.isAddFeesMode
        ? 'deductFees'
        : 'addFees';
      this.amountModeChange.emit(next);
      return;
    }
    this.toggleCurrency();
  }

  toggleCurrency(): void {
    this.isCurrency = !this.isCurrency;
    this.currencyChange.emit(this.isCurrency);
  }

  onFocus(event: FocusEvent): void {
    this.focus.emit(event);
  }

  onBlur(event: FocusEvent): void {
    this.blur.emit(event);

    this.control.markAsTouched();

    if (this.type === 'number' && this.format === 'currency') {
      const value = this.control.value;
      const normalized = this.parseLooseMoneyString(
        this.formatCurrencyDisplay(value),
      );
      this.control.setValue(Number.isFinite(normalized) ? normalized : null);
    }

    if (this.type === 'decimal' || this.type === 'rate') {
      const parsed = this.parseDecimal(this.control.value);
      this.control.setValue(parsed);
    }
  }

  private formatCurrencyDisplay(value: unknown): string {
    if (value === null || value === undefined || value === '') return '';
    let n: number;
    if (typeof value === 'number') {
      n = value;
    } else {
      n = this.parseDecimal(value) ?? NaN;
    }
    if (!Number.isFinite(n)) return '';
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: this.currencyCode,
    }).format(n);
  }

  /** Normalize Intl-formatted currency strings (legacy number + format=currency path). */
  private parseLooseMoneyString(value: string): number {
    if (!value) return NaN;
    const numeric = value.replace(/[^\d,]/g, '').replace(',', '.');
    return parseFloat(numeric);
  }

  private parseDecimal(value: unknown): number | null {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;

    const raw = String(value).trim().replace(/\s/g, '');
    if (!raw) return null;

    const lastComma = raw.lastIndexOf(',');
    const lastDot = raw.lastIndexOf('.');
    const decimalSeparator =
      lastComma > lastDot ? ',' : lastDot > lastComma ? '.' : null;

    const normalized = decimalSeparator
      ? raw
          .replace(/[.,]/g, match => (match === decimalSeparator ? '@' : ''))
          .replace('@', '.')
      : raw.replace(/[.,]/g, '');

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }
}
